import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { api } from '@/services/api'
import { useCurrentOrgId } from '@/hooks/useCurrentOrgId'
import { Card, StatCard, Table, ConfidenceBadge } from '@/components/ui'
import { TrialBanner } from '@/components/billing/TrialBanner'
import { TermsUpdateBanner } from '@/components/layout'
import { EmptyDashboard, SampleDetectionBanner } from '@/components/onboarding'
import { isSampleDismissed } from '@/components/onboarding/SampleDetectionBanner'
import { formatRelativeTime } from '@/utils/format-date'
import type { DetectionResponse, DashboardStats } from '@spoofio/shared/types'

export function DashboardPage() {
  const { orgId } = useCurrentOrgId()

  const { data: stats, isLoading, error } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: () => api.getDashboardStats(),
  })

  const { data: billingStatus } = useQuery({
    queryKey: ['billing-status', orgId],
    queryFn: () => api.getBillingStatus(orgId ?? ''),
    enabled: !!orgId,
  })

  const { data: profile } = useQuery({
    queryKey: ['user-profile'],
    queryFn: () => api.getUserProfile(),
  })

  if (isLoading) {
    return <DashboardSkeleton />
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-danger-600">Failed to load dashboard data</p>
      </div>
    )
  }

  if (!stats) {
    return null
  }

  // Show empty state when organization has no canaries
  if (stats.totalCanaries === 0) {
    return (
      <div className="space-y-6">
        {billingStatus && <TrialBanner billingStatus={billingStatus} />}
      <TermsUpdateBanner />
        <EmptyDashboard activatedAt={billingStatus?.organization.activatedAt} />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {billingStatus && <TrialBanner billingStatus={billingStatus} />}
      <TermsUpdateBanner />

      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <Link to="/canaries/new" className="btn btn-primary">
          Create Canary
        </Link>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Total Canaries"
          value={stats.totalCanaries}
          icon={
            <svg className="h-6 w-6 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
          }
        />
        <StatCard
          title="Active Canaries"
          value={stats.activeCanaries}
          icon={
            <svg className="h-6 w-6 text-success-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          }
        />
        <StatCard
          title="Detections (24h)"
          value={stats.detections24h}
          change={`${stats.detections7d} in 7d`}
          changeType="neutral"
          icon={
            <svg className="h-6 w-6 text-warning-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          }
        />
        <StatCard
          title="Alerts Sent (24h)"
          value={stats.alertsSent24h}
          icon={
            <svg className="h-6 w-6 text-danger-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
          }
        />
      </div>

      {shouldShowSample({
        stats,
        orgId,
        activatedAt: billingStatus?.organization.activatedAt ?? null,
        isPlatformAdmin: profile?.isPlatformAdmin ?? false,
      }) && orgId && (
        <SampleDetectionBanner organizationId={orgId} />
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Recent Detections */}
        <Card
          title="Recent Detections"
          actions={
            <Link to="/detections" className="text-sm text-primary-600 hover:text-primary-700">
              View all
            </Link>
          }
        >
          <RecentDetectionsTable detections={stats.recentDetections} />
        </Card>

        {/* Top Fake Domains */}
        <Card
          title="Top Fake Domains"
          description="Most frequently detected unauthorized domains"
        >
          <TopFakeDomainsTable domains={stats.topFakeDomains} />
        </Card>
      </div>
    </div>
  )
}

const SAMPLE_VISIBLE_DAYS = 7
const MS_PER_DAY = 1000 * 60 * 60 * 24

function shouldShowSample(opts: {
  stats: DashboardStats
  orgId: string | undefined
  activatedAt: string | null
  isPlatformAdmin: boolean
}): boolean {
  const { stats, orgId, activatedAt, isPlatformAdmin } = opts
  if (isPlatformAdmin) return false
  if (!orgId) return false
  if (stats.totalCanaries === 0) return false
  if (stats.detections24h > 0 || stats.detections7d > 0) return false
  if (isSampleDismissed(orgId)) return false
  if (!activatedAt) return true
  const ageDays = (Date.now() - new Date(activatedAt).getTime()) / MS_PER_DAY
  return ageDays <= SAMPLE_VISIBLE_DAYS
}

function RecentDetectionsTable({ detections }: { detections: DetectionResponse[] }) {
  const columns = [
    {
      key: 'fakeFqdn',
      header: 'Fake Domain',
      render: (d: DetectionResponse) => (
        <span className="font-mono text-sm">{d.fakeFqdn}</span>
      ),
    },
    {
      key: 'canaryName',
      header: 'Canary',
      render: (d: DetectionResponse) => (
        <Link
          to={`/canaries/${d.canaryId}`}
          className="text-primary-600 hover:text-primary-700"
        >
          {d.canaryName}
        </Link>
      ),
    },
    {
      key: 'confidenceScore',
      header: 'Confidence',
      render: (d: DetectionResponse) => (
        <ConfidenceBadge score={d.confidenceScore} />
      ),
    },
    {
      key: 'detectedAt',
      header: 'Time',
      render: (d: DetectionResponse) => formatRelativeTime(d.detectedAt),
    },
  ]

  return (
    <Table
      columns={columns}
      data={detections}
      keyExtractor={(d) => d.id}
      emptyMessage="No detections yet"
    />
  )
}

function TopFakeDomainsTable({
  domains,
}: {
  domains: DashboardStats['topFakeDomains']
}) {
  if (domains.length === 0) {
    return (
      <p className="text-gray-500 text-sm text-center py-6">
        No fake domains detected yet
      </p>
    )
  }

  return (
    <div className="space-y-3">
      {domains.map((domain) => (
        <div
          key={domain.fakeFqdn}
          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
        >
          <div>
            <p className="font-mono text-sm text-gray-900">{domain.fakeFqdn}</p>
            <p className="text-xs text-gray-500">
              Last seen: {formatRelativeTime(domain.lastSeen)}
            </p>
          </div>
          <span className="badge badge-danger">{domain.count} detections</span>
        </div>
      ))}
    </div>
  )
}

function DashboardSkeleton() {
  return (
    <div className="space-y-6">
      <div className="h-8 bg-gray-200 rounded w-48 animate-pulse" />
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }, (_, i) => (
          <div key={i} className="bg-white shadow rounded-lg p-6 animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-24 mb-2" />
            <div className="h-8 bg-gray-200 rounded w-16" />
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="bg-white shadow rounded-lg p-6 animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-40 mb-4" />
          <div className="space-y-3">
            {Array.from({ length: 5 }, (_, i) => (
              <div key={i} className="h-12 bg-gray-100 rounded" />
            ))}
          </div>
        </div>
        <div className="bg-white shadow rounded-lg p-6 animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-40 mb-4" />
          <div className="space-y-3">
            {Array.from({ length: 5 }, (_, i) => (
              <div key={i} className="h-12 bg-gray-100 rounded" />
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

